
import React, {
  useState,
  useEffect,
  useCallback
} from "react"

import axios from "axios"
import Cookies from "js-cookie"
import {
  formatRemainingTime,
  formatStockQuantity,
  getFoodImageUrl,
  getFoodStatusLabel,
  getStockQuantity,
  isLimitedCategory,
  toDateTimeLocal
} from "../utils/foodAvailability"

const API = "https://food-backend-9733.onrender.com"

const Admin = () => {
  const token = JSON.parse(
    Cookies.get("logincrd") || "{}"
  )?.token

  const emptyForm = {
    name: "",
    description: "",
    product_details: "",
    price: "",
    price_250: "",
    price_500: "",
    price_1kg: "",
    category: "normal",
    stock_quantity: "20",
    availability_status: "active",
    availability_hours: "",
    expires_at: ""
  }

  const [foods, setFoods] = useState([])
  const [images, setImages] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [data, setData] = useState(emptyForm)

  const loadFoods = useCallback(() => {
    axios.get(`${API}/admin/foods`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => {
        setFoods(res.data)
      })
      .catch((err) => {
        console.log("Load foods error:", err)
      })
  }, [token])

  useEffect(() => {
    loadFoods()
  }, [loadFoods])

  const handleChange = (e) => {
    const { name, value } = e.target

    const nextData = {
      ...data,
      [name]: value
    }

    if (name === "category") {
      if (value === "48hours") {
        nextData.availability_hours = "48"
        nextData.price_500 = ""
        nextData.price_1kg = ""
      } else if (value === "weekly") {
        nextData.availability_hours = "168"
        nextData.price_500 = ""
        nextData.price_1kg = ""
      } else {
        nextData.availability_hours = ""
      }

      nextData.expires_at = ""
    }

    if (
      name === "availability_status" &&
      value === "out_of_stock"
    ) {
      nextData.stock_quantity = "0"
    }

    setData(nextData)
  }

  const resetForm = () => {
    setData({ ...emptyForm })
    setImages([])

    const fileInput = document.getElementById("foodImage")

    if (fileInput) {
      fileInput.value = ""
    }
  }

  const appendFoodFormData = () => {
    const formData = new FormData()
    const limitedForm = isLimitedCategory(data.category)

    const fallback250Price = limitedForm
      ? Number(data.price || 0) * 2.5
      : data.price

    formData.append("name", data.name)
    formData.append("description", data.description)
    formData.append(
      "product_details",
      data.product_details || ""
    )
    formData.append("price", data.price)
    formData.append(
      "price_250",
      data.price_250 || fallback250Price
    )
    formData.append(
      "price_500",
      data.price_500 || Number(data.price || 0) * 2
    )
    formData.append(
      "price_1kg",
      data.price_1kg || Number(data.price || 0) * 4
    )
    formData.append("category", data.category || "normal")
    formData.append(
      "stock_quantity",
      data.stock_quantity || 0
    )
    formData.append(
      "availability_status",
      data.availability_status || "active"
    )
    formData.append(
      "availability_hours",
      data.availability_hours || ""
    )
    formData.append("expires_at", data.expires_at || "")

    if (images[0]) {
      formData.append("image", images[0])
    }

    images.slice(1, 3).forEach((file) => {
      formData.append("images", file)
    })

    return formData
  }

  const addFood = () => {
    if (
      !data.name ||
      !data.description ||
      !data.price ||
      images.length === 0
    ) {
      alert("All fields required")
      return
    }

    const formData = appendFoodFormData()

    axios.post(`${API}/admin/add-food`, formData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => {
        alert(res.data.msg || "Food added successfully")
        loadFoods()
        resetForm()
      })
      .catch((err) => {
        console.log("Add food error:", err)
        alert(err.response?.data?.msg || "Add food failed")
      })
  }

  const deleteFood = (id) => {
    const confirmDelete = window.confirm("Delete this food?")

    if (!confirmDelete) return

    axios.delete(`${API}/admin/delete-food/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => {
        alert(res.data.msg || "Food deleted successfully")
        loadFoods()
      })
      .catch((err) => {
        console.log("Delete food error:", err)
        alert(err.response?.data?.msg || "Delete failed")
      })
  }

  const editFood = (item) => {
    setEditingId(item.id)

    setData({
      name: item.name || "",
      description: item.description || "",
      product_details: item.product_details || "",
      price: item.price ?? "",
      price_250: item.price_250 ?? item.price ?? "",
      price_500:
        item.price_500 ?? Number(item.price || 0) * 2,
      price_1kg:
        item.price_1kg ?? Number(item.price || 0) * 4,
      category: item.category || "normal",
      stock_quantity: getStockQuantity(item) ?? "",
      availability_status:
        Number(item.is_visible) === 0
          ? "hidden"
          : Number(item.is_out_of_stock) === 1
            ? "out_of_stock"
            : "active",
      availability_hours: item.availability_hours || "",
      expires_at: toDateTimeLocal(item.expires_at)
    })

    setImages([])

    const fileInput = document.getElementById("foodImage")

    if (fileInput) {
      fileInput.value = ""
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  }

  const updateFood = () => {
    if (
      !data.name ||
      !data.description ||
      !data.price
    ) {
      alert("All fields required")
      return
    }

    if (!editingId) {
      alert("Please select a food item to update")
      return
    }

    const formData = appendFoodFormData()

    axios.put(
      `${API}/admin/update-food/${editingId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
      .then((res) => {
        alert(res.data.msg || "Food updated successfully")
        loadFoods()
        setEditingId(null)
        resetForm()
      })
      .catch((err) => {
        console.log("Update food error:", err)
        alert(err.response?.data?.msg || "Update failed")
      })
  }

  const cancelEdit = () => {
    setEditingId(null)
    resetForm()
  }

  const activeFoods = foods.filter((item) =>
    Number(item.is_visible) !== 0 &&
    Number(item.is_expired) !== 1 &&
    Number(item.is_out_of_stock) !== 1 &&
    (
      getStockQuantity(item) === null ||
      getStockQuantity(item) > 0
    )
  ).length

  const outOfStockFoods = foods.filter((item) =>
    Number(item.is_out_of_stock) === 1 ||
    (
      getStockQuantity(item) !== null &&
      getStockQuantity(item) <= 0
    )
  ).length

  const hiddenFoods = foods.filter((item) =>
    Number(item.is_visible) === 0 ||
    Number(item.is_expired) === 1
  ).length

  const isLimitedForm = isLimitedCategory(data.category)

  return (
    <div className="admin-food-page">
      <div className="admin-food-header">
        <div>
          <h1>Admin Food Panel</h1>
          <p>Add, edit and manage all homemade food items.</p>
        </div>

        <div className="admin-food-count-grid">
          <div className="admin-food-count">
            <span>{foods.length}</span>
            <p>Total Foods</p>
          </div>

          <div className="admin-food-count">
            <span>{activeFoods}</span>
            <p>Active</p>
          </div>

          <div className="admin-food-count">
            <span>{outOfStockFoods}</span>
            <p>Out of Stock</p>
          </div>

          <div className="admin-food-count">
            <span>{hiddenFoods}</span>
            <p>Hidden/Expired</p>
          </div>
        </div>
      </div>

      <div className="admin-food-form-card">
        <h2>
          {editingId ? "Update Food Item" : "Add New Food"}
        </h2>

        <div className="admin-form-grid">
          <input
            type="text"
            name="name"
            placeholder="Food Name"
            value={data.name}
            onChange={handleChange}
          />

          <select
            name="category"
            value={data.category}
            onChange={handleChange}
          >
            <option value="normal">Normal Foods</option>
            <option value="cookies">Cookies</option>
            <option value="48hours">48 Hours Foods</option>
            <option value="weekly">Weekly Items</option>
            <option value="snacks">Snacks</option>
            <option value="delights">Delights</option>
            <option value="desserts">Desserts</option>
            <option value="mixtures">Mixtures</option>
          </select>

          <input
            type="number"
            name="price"
            placeholder={
              isLimitedForm ? "100 grams price" : "Base Price"
            }
            value={data.price}
            onChange={handleChange}
          />

          <input
            type="number"
            name="price_250"
            placeholder="250 grams price"
            value={data.price_250}
            onChange={handleChange}
          />

          {!isLimitedForm && (
            <>
              <input
                type="number"
                name="price_500"
                placeholder="500 grams price"
                value={data.price_500}
                onChange={handleChange}
              />

              <input
                type="number"
                name="price_1kg"
                placeholder="1 kg price"
                value={data.price_1kg}
                onChange={handleChange}
              />
            </>
          )}

          <input
            type="number"
            name="stock_quantity"
            min="0"
            placeholder={
              isLimitedForm
                ? "Available stock in grams"
                : "Available item count"
            }
            value={data.stock_quantity}
            onChange={handleChange}
          />

          <select
            name="availability_status"
            value={data.availability_status}
            onChange={handleChange}
          >
            <option value="active">Available to Users</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="hidden">Disappear from Website</option>
          </select>

          <input
            type="number"
            name="availability_hours"
            min="1"
            placeholder="Auto expiry hours"
            value={data.availability_hours}
            onChange={handleChange}
          />

          <input
            type="datetime-local"
            name="expires_at"
            value={data.expires_at}
            onChange={handleChange}
          />

          <label className="admin-file-box">
            <input
              id="foodImage"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                setImages(
                  Array.from(e.target.files || []).slice(0, 3)
                )
              }}
            />

            <span>
              {images.length > 0
                ? images.map((file) => file.name).join(", ")
                : editingId
                  ? "Choose new images only if you want to replace them"
                  : "Upload 1 to 3 Food Images"}
            </span>
          </label>
        </div>

        <textarea
          name="description"
          placeholder="Food Description"
          value={data.description}
          onChange={handleChange}
          className="admin-description"
        />

        <textarea
          name="product_details"
          placeholder={
            "Product Details (one point per line)\nExample:\nFresh homemade food\nPrepared after order confirmation"
          }
          value={data.product_details}
          onChange={handleChange}
          className="admin-description"
        />

        <div className="admin-form-actions">
          {editingId ? (
            <>
              <button
                className="admin-primary-btn"
                onClick={updateFood}
              >
                Update Food
              </button>

              <button
                className="admin-secondary-btn"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="admin-primary-btn"
              onClick={addFood}
            >
              Add Food
            </button>
          )}
        </div>
      </div>

      <div className="admin-section-title">
        <h2>All Foods</h2>
        <p>Manage categories, prices, images and product details.</p>
      </div>

      <div className="admin-food-grid">
        {foods.map((item) => {
          const statusLabel = getFoodStatusLabel(item)
          const timerText = formatRemainingTime(item)
          const itemLimited = isLimitedCategory(item.category)

          return (
            <div
              key={item.id}
              className={
                statusLabel === "Available"
                  ? "admin-food-card"
                  : "admin-food-card admin-food-card-muted"
              }
            >
              <img
                src={getFoodImageUrl(item, API)}
                alt={item.name}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = "/logo192.png"
                }}
              />

              <div className="admin-food-card-body">
                <div className="admin-badge-row">
                  <span className="admin-category-badge">
                    {item.category || "normal"}
                  </span>

                  <span
                    className={
                      statusLabel === "Available"
                        ? "admin-status-badge"
                        : "admin-status-badge danger"
                    }
                  >
                    {statusLabel}
                  </span>
                </div>

                <h3>{item.name}</h3>

                <p className="admin-food-desc">
                  {item.description}
                </p>

                <div className="admin-stock-row">
                  <span>
                    Stock:{" "}
                    <b>
                      {itemLimited
                        ? formatStockQuantity(item, "grams")
                        : formatStockQuantity(item, "items")}
                    </b>
                  </span>

                  <span>{timerText || "No expiry"}</span>
                </div>

                <div className="admin-price-list">
                  <p>
                    <span>{itemLimited ? "100g" : "Base"}</span>
                    <b>₹ {item.price}</b>
                  </p>

                  <p>
                    <span>250g</span>
                    <b>₹ {item.price_250 || item.price}</b>
                  </p>

                  {!itemLimited && (
                    <>
                      <p>
                        <span>500g</span>
                        <b>
                          ₹ {item.price_500 || Number(item.price || 0) * 2}
                        </b>
                      </p>

                      <p>
                        <span>1kg</span>
                        <b>
                          ₹ {item.price_1kg || Number(item.price || 0) * 4}
                        </b>
                      </p>
                    </>
                  )}
                </div>

                <div className="admin-card-actions">
                  <button
                    className="admin-edit-btn"
                    onClick={() => editFood(item)}
                  >
                    Edit
                  </button>

                  <button
                    className="admin-delete-btn"
                    onClick={() => deleteFood(item.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Admin
